import { IUser } from 'atoms';
import { apiGet } from '../../services/_common';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import styled, { css } from 'styled-components';
import { useEffect, useState } from 'react';
import IssueGuide from '../IssueGuide';
import SkeletonTable from '../SkeletonTable';
import { useTranslation } from 'react-i18next';

const MAX_LIST_LIMIT = 10; // 데이터 조회/표시 최대 개수

const Root = styled.div<{ gridColumnCnt: number; isStacked?: boolean; hasCard?: boolean }>`
  height: 100%;

  .widget-header {
    padding: 1.5rem 1rem 0 1rem;
    > .widget-title {
      > span {
        color: var(--widget-text-primary);
        font-size: var(--widget-font-size-title);
      }
    }
  }

  .widget-body {
    padding: 1.5rem;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: ${(props) => (props.isStacked ? 'column' : props.hasCard && props.gridColumnCnt === 5 ? 'row' : 'column')};
    gap: 0.5rem;

    .recent-attend-info-item-container {
      display: flex;
      flex-direction: column;
      flex: ${(props) => (props.isStacked ? 'none' : props.gridColumnCnt === 5 ? '0.3' : '')};
      ${(props) => props.isStacked && 'width: 100%;'}

      .item-title {
        width: 100%;
        text-align: center;
        justify-content: center;
        margin-bottom: 1rem;

        h3 {
          padding: 0.5rem 0;
          text-align: center;
          margin: 0;
          color: var(--white);
          font-size: var(--widget-font-size-2xl);
          font-weight: var(--font-bold);
          background-color: var(--widget-primary);
          border-radius: 1rem;
        }
      }
    }

    .recent-attend-info-item {
      background: var(--widget-theme-tonal-blue);
      border-radius: 1rem;
      padding: 1rem;
      flex-grow: ${(props) => (props.isStacked ? 0 : 1)};
      gap: 0.5rem;
      display: flex;
      flex-direction: column;
      justify-content: ${(props) => (props.gridColumnCnt === 3 ? 'space-around' : '')};

      > div {
        display: flex;
        flex-direction: row;
        gap: 0.75rem;
        align-items: center;
        padding: 0.5rem 0;

        dt {
          font-weight: var(--font-bold);
          color: var(--widget-text-primary);
          font-size: ${(props) => (props.gridColumnCnt === 5 ? 'var(--widget-font-size-lg)' : 'var(--widget-font-size-xl)')};
          flex-shrink: 0;
          opacity: 0.8;
          width: 6rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: break-spaces;
        }

        dd {
          color: var(--widget-text-primary);
          font-size: var(--widget-font-size-xl);
          font-weight: var(--font-medium);
          flex: 1;
          overflow: visible;
          text-overflow: clip;
          white-space: normal;
          word-break: break-word;
          line-height: 1;
        }
      }
    }

    .recent-attend-info-item-list-header {
      display: grid;
      grid-template-columns: repeat(${(props) => props.gridColumnCnt}, 1fr); /* 5컬럼 동일 비율 */
      gap: 0.5rem;
      align-items: center;
    }

    .recent-attend-info-item-list {
      display: flex;
      flex-direction: column;
      flex: ${(props) => (props.isStacked ? '1' : props.hasCard && props.gridColumnCnt === 5 ? '0.7' : '1')};
      height: 100%;
      justify-content: flex-start;
      text-align: center;

      &.full-height {
        justify-content: space-between;
      }

      .recent-attend-info-header {
        color: var(--widget-text-primary);
      }

      .recent-attend-info-item-row {
        color: var(--widget-text-secondary);
      }

      .recent-attend-info-header,
      .recent-attend-info-item-row {
        display: grid;
        grid-template-columns: ${(props) =>
          props.isStacked
            ? props.gridColumnCnt === 5
              ? '1.3fr 0.9fr 1fr 1.4fr' /* 상황판 세로형: 협력업체/이름/출입시간/출입위치 (체온 제외) */
              : '1.1fr 1.2fr 1.3fr' /* 상황판 세로형(회사): 이름/출입시간/출입위치 (체온 제외) */
            : props.gridColumnCnt === 5
            ? '1.2fr 0.8fr 1fr 1.3fr 0.7fr'
            : '0.4fr 1.1fr 1.3fr 0.5fr'};
        gap: 0.25rem;
        align-items: center;
      }

      .recent-attend-info-header {
        font-weight: var(--font-bold);
        padding: 0 0 1rem 0;
        border-bottom: 1px solid var(--outline);

        > div {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: var(--widget-font-size-lg);
          line-height: 1.2;
          font-weight: 600;
        }
      }

      .recent-attend-info-item-row {
        padding: 0.5rem 0;
        border-radius: 6px;
        transition: background-color 200ms ease;

        > div {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: var(--widget-font-size-lg);
          line-height: 1.2;

          &:first-child {
            text-align: start;
            padding-left: 1rem;
          }
        }

        &.highlight {
          background-color: var(--widget-theme-tonal-blue);
          position: relative;

          &::before {
            content: '|';
            position: absolute;
            left: 0.1rem;
            top: 50%;
            transform: translateY(-50%);
            color: var(--widget-primary);
            font-weight: var(--font-bold);
            font-size: 2.5em;
          }
        }
      }
    }
  }
  .noData {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    height: 100%;
    color: var(--text-primary);
    font-size: var(--widget-font-size-lg);
    font-weight: 400;

    span {
      color: var(--widget-sub-text);
      font-size: var(--widget-font-size-5xl);

      &.material-symbols-rounded {
        font-variation-settings: 'FILL' 1, 'wght' 600, 'GRAD' 200, 'opsz' 40;
      }
    }
  }

  ${(props) =>
    props.isStacked &&
    css`
      .widget-body {
        /* stacked는 강조 카드 없이 타이틀 + 목록만 표시 */
        .recent-attend-info-item-container {
          flex: none;
          margin-bottom: 0.5rem;

          .item-title {
            margin-bottom: 0;

            h3 {
              font-size: var(--widget-font-size-xl);
            }
          }
        }

        /* 목록: 항상 상단부터 자연 높이로 쌓음. 행이 적어도 가운데로 벌어지지 않음 */
        .recent-attend-info-item-list {
          flex: 1;
          height: auto;
          min-height: 0;
          justify-content: flex-start;

          /* 헤더↔보더 간격을 타이틀↔헤더 간격(1rem)과 동일하게 맞춤 */
          .recent-attend-info-header {
            flex: 0 0 auto;
            padding: 0 0 1rem 0;
            margin-bottom: 0.25rem;
          }

          /* 자연 높이 유지. 공간이 부족할 때만 줄어들고(안 잘림), 남아도 늘어나지 않음(상단 정렬) */
          .recent-attend-info-item-row {
            flex: 0 1 auto;
            min-height: 0;
            padding: 0.6rem 0;
            overflow: hidden;
          }
        }
      }
    `}
`;

// 실시간 출입 현황
export const RecentAttendInfo = ({ userInfo, uniqueKey, refetchInterval }: { userInfo: IUser; uniqueKey: string; refetchInterval: number }) => {
  const isSiteDashboard = refetchInterval !== undefined; // 상황판여부
  const isLargeSize = uniqueKey === '126';
  const isStacked = uniqueKey === '156'; // 세로 스택(상단 카드) 버전
  const hasFeatureCard = isLargeSize || isStacked; // 강조 카드(순환) 표시 여부
  const { hCd, sCd } = userInfo;
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState<number>(0); // 5초마다 선택 인덱스 순환
  const isSite = hCd?.substring(0, 1).toLowerCase() === 'h' && sCd !== '00000' && sCd !== '';
  const isCompany = hCd?.substring(0, 1).toLowerCase() === 'c' && sCd === '00000';

  const {
    data: attendData,
    isLoading: isLoadingAttendData,
    isError: isErrorAttendData,
  } = useQuery(['attendCntGet', uniqueKey, userInfo], () => fetchAttendData(), {
    enabled: !!hCd && (isSite || isCompany),
    ...(isSiteDashboard && { refetchInterval }), // 1분
  });

  const fetchAttendData = async () => {
    try {
      const { startDate, endDate } = getDateRange();
      const path = isSite ? '/attend/day' : '/attend/day/company';
      const req = { aDate1: startDate, aDate2: endDate, ...(isSite ? { hCd, sCd } : { cCd: hCd }) };
      const res = await apiGet({ path, req });
      if (res.status === 200) {
        const { attendList } = res.data.data;

        // aDate(YYYY-MM-DD)와 aTimeMax(HH:MM:SS)를 결합해 날짜+시간 기준 최신순 정렬 후 상위 10개 추출
        const recentAttendTop10 = [...(attendList || [])]
          .filter((x: any) => !!x.aDate && !!x.aTimeMax)
          .sort((a: any, b: any) => {
            const aKey = `${String(a.aDate).replace(/-/g, '')}${String(a.aTimeMax).replace(/:/g, '')}`;
            const bKey = `${String(b.aDate).replace(/-/g, '')}${String(b.aTimeMax).replace(/:/g, '')}`;
            return bKey.localeCompare(aKey); // 최신순 (내림차순)
          })
          .slice(0, MAX_LIST_LIMIT);

        return recentAttendTop10;
      }
      return undefined;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  // 공통 날짜 범위 계산 함수
  const getDateRange = () => {
    const today = dayjs();
    return { startDate: today.format('YYYYMMDD'), endDate: today.format('YYYYMMDD') };
  };

  const displayData = (attendData ?? []).slice(0, MAX_LIST_LIMIT);

  // 리페치로 목록이 줄어 선택 인덱스가 범위를 벗어나면 보정 (카드 공백·하이라이트 누락 방지)
  useEffect(() => {
    if (selectedIndex >= displayData.length) setSelectedIndex(0);
  }, [displayData.length]);

  useEffect(() => {
    if (!displayData.length || !isLargeSize) {
      return undefined;
    }

    const timer = setInterval(() => {
      setSelectedIndex((prev) => (prev + 1) % displayData.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [displayData.length]);

  const selected = displayData[selectedIndex];

  if (isErrorAttendData) return <IssueGuide />;
  if (isLoadingAttendData) return <SkeletonTable rows={10} />;

  return (
    <Root gridColumnCnt={isSite ? 5 : 3} isStacked={isStacked} hasCard={isLargeSize}>
      <div className='widget-body'>
        {hasFeatureCard && (
          <div className='recent-attend-info-item-container'>
            <div className='item-title'>
              <h3>{t('실시간 출입 현황')}</h3>
            </div>
            {/* 강조 카드는 large(126)에서만 사용. stacked(156)는 타이틀 + 목록만 표시 */}
            {isLargeSize && (
              <div className='recent-attend-info-item'>
                <div>
                  <dt>{t('이름')}</dt>
                  <dd>{selected?.wName ?? '-'}</dd>
                </div>
                {isSite && (
                  <div>
                    <dt>{t('협력업체')}</dt>
                    <dd>{selected?.sjName ?? '-'}</dd>
                  </div>
                )}
                <div>
                  <dt>{t('출입 시간')}</dt>
                  <dd>{selected?.aTimeMax ?? '-'}</dd>
                </div>
                <div>
                  <dt>{t('출입 위치')}</dt>
                  <dd>{selected?.fLocationName ?? '-'}</dd>
                </div>
                <div>
                  <dt>{t('체온')}</dt>
                  <dd>{selected?.tempMax ?? '-'}</dd>
                </div>
              </div>
            )}
          </div>
        )}
        <div className={`recent-attend-info-item-list ${!isStacked && displayData.length >= MAX_LIST_LIMIT ? 'full-height' : ''}`}>
          <div className='recent-attend-info-header'>
            {isSite && <div>{t('협력업체')}</div>}
            <div>{t('이름')}</div>
            <div>{t('출입 시간')}</div>
            <div>{t('출입 위치')}</div>
            {!isStacked && <div>{t('체온')}</div>}
          </div>
          {displayData.length > 0 ? (
            displayData.map((el, idx) => (
              <div key={el.wCd ?? `${el.wName}-${el.aDate}-${el.aTimeMax}-${el.fLocationName}`} className={`recent-attend-info-item-row ${isLargeSize && idx === selectedIndex ? 'highlight' : ''}`}>
                {isSite && <div>{el.sjName}</div>}
                <div>{el.wName}</div>
                <div>{el.aTimeMax}</div>
                <div>{el.fLocationName}</div>
                {!isStacked && <div>{el.tempMax}</div>}
              </div>
            ))
          ) : (
            <div className='noData'>
              <span className='material-symbols-rounded'>person_off</span>
              {t('근로자 출입이 없습니다')}
            </div>
          )}
        </div>
      </div>
    </Root>
  );
};
